import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Car, Shield, Calculator } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage, Language } from "@/hooks/use-language";
import { getTranslation } from "@/lib/translations";

const getServices = (language: Language) => [
  {
    id: "company",
    title: getTranslation("businessServices", language),
    description: getTranslation("businessServicesDesc", language),
    icon: Briefcase,
    badge: getTranslation("popular", language),
    features: [
      getTranslation("companyRegistration", language), 
      getTranslation("krsChanges", language), 
      getTranslation("virtualAddress", language)
    ],
    imageUrl: "https://storage.googleapis.com/fenado-ai-farm-public/generated/39e3d33c-72aa-416d-9cdc-61fb5416b406.webp"
  },
  {
    id: "auto",
    title: getTranslation("vehicleRegistration", language),
    description: getTranslation("vehicleRegistrationDesc", language),
    icon: Car,
    features: [
      getTranslation("euCarRegistration", language), 
      getTranslation("nonEuCarRegistration", language), 
      getTranslation("insurance", language)
    ],
    imageUrl: "https://storage.googleapis.com/fenado-ai-farm-public/generated/50ad3600-d3b5-4183-93df-3da9f9c4f550.webp"
  },
  {
    id: "support",
    title: getTranslation("residencePermit", language),
    description: getTranslation("residencePermitDesc", language),
    icon: Shield,
    badge: getTranslation("fastImplementation", language),
    features: [
      getTranslation("legalizeResidence", language), 
      getTranslation("obtainPesel", language), 
      getTranslation("representationBeforeOffices", language)
    ],
    imageUrl: "https://storage.googleapis.com/fenado-ai-farm-public/generated/f2780297-e72a-430f-84cb-a0d9330862f2.webp"
  },
  {
    id: "tax",
    title: getTranslation("accountingTax", language),
    description: getTranslation("accountingTaxDesc", language),
    icon: Calculator,
    features: [
      getTranslation("basicPackage", language), 
      getTranslation("standardPackage", language), 
      getTranslation("premiumPackage", language)
    ],
    imageUrl: "https://storage.googleapis.com/fenado-ai-farm-public/generated/ba8dc7c4-40c9-4909-be50-209b22b15cb4.webp"
  }
];

export default function ServicesSection() {
  const { language } = useLanguage();
  const services = getServices(language);
  
  return (
    <section className="py-16 sm:py-20 md:py-24 lg:py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center z-0" style={{ backgroundImage: 'url(https://storage.googleapis.com/fenado-ai-farm-public/generated/84a953de-555d-4aeb-b81d-9b10c1552179.webp)' }}></div>
      <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/70 to-background/90 z-0"></div>
      <div className="absolute inset-0 dot-pattern opacity-20 z-0"></div>
      <div className="container relative z-10 px-4 sm:px-6 md:px-8">
        <div className="text-center mb-12 md:mb-16 max-w-3xl mx-auto">
          <Badge variant="outline" className="mb-3 md:mb-4 px-3 sm:px-4 py-1 text-sm bg-primary/10 text-primary border-primary/20 backdrop-blur-sm">
            {getTranslation("ourServices", language)}
          </Badge>
          <div className="relative mb-6 sm:mb-8">
            <h2 className="section-heading relative text-2xl sm:text-3xl md:text-4xl px-2 sm:px-0">
              <span className="text-gradient-help">{getTranslation("specialistSupport", language)}</span> <span className="text-gradient-help">{getTranslation("inKeyAreas", language)}</span>
            </h2>
          </div>

          <p className="section-subheading text-base sm:text-lg md:text-xl mb-8 md:mb-12 px-2 sm:px-4">
            {getTranslation("servicesComprehensiveDescription", language)}
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {services.map((service, index) => (
            <div 
              key={service.id} 
              className="relative h-[380px] sm:h-[400px] md:h-[420px] rounded-lg overflow-hidden animate-fade-in-up shadow-xl group" 
              style={{animationDelay: `${index * 100}ms`}}
            >
              <div className="absolute inset-0">
                <img 
                  src={service.imageUrl} 
                  alt={service.title} 
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 brightness-90"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/85 to-black/75 group-hover:opacity-95 transition-opacity duration-300"></div>
                <div className="absolute inset-0 bg-black/30"></div>
                <div className="absolute inset-0 bg-primary/20 opacity-0 group-hover:opacity-20 transition-opacity duration-300"></div>
              </div>
              <div className="relative h-full flex flex-col p-4 sm:p-5 md:p-6 text-white z-10">
                {service.badge && (
                  <Badge className="self-end mb-3 sm:mb-4 bg-primary/40 text-white border-none hover:bg-primary/50 shadow-lg backdrop-blur-sm text-xs sm:text-sm">{service.badge}</Badge>
                )}
                <div className="mt-auto">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <div className="p-2 sm:p-2.5 rounded-lg bg-primary/50 backdrop-blur-sm shadow-md group-hover:bg-primary/70 transition-colors duration-300">
                      <service.icon className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-heading font-bold text-white group-hover:text-primary-foreground transition-colors duration-300">{service.title}</h3>
                  </div>
                  <p className="text-white mb-3 sm:mb-4 line-clamp-3 text-sm sm:text-base font-semibold group-hover:text-white transition-colors duration-300">
                    {service.description}
                  </p>
                  <div className="space-y-2 sm:space-y-2.5 mb-4 sm:mb-6">
                    {service.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center">
                        <div className="h-2.5 w-2.5 sm:h-3 sm:w-3 rounded-full bg-primary mr-2 sm:mr-3 group-hover:bg-primary/80 transition-colors duration-300 shadow-glow"></div>
                        <span className="text-xs sm:text-sm text-white font-semibold group-hover:text-white transition-colors duration-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <Button asChild variant="outline" className="w-full gap-1 sm:gap-2 rounded-lg border-white/40 text-white hover:bg-primary/30 hover:border-primary/50 hover:text-white shadow-md backdrop-blur-sm text-sm sm:text-base py-1.5 sm:py-2">
                    <Link to={`/uslugi#${service.id}`}>
                      {getTranslation("learnMore", language)} <ArrowRight className="h-3 w-3 sm:h-4 sm:w-4 transition-transform group-hover:translate-x-1" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="mt-10 sm:mt-12 md:mt-16 text-center relative z-10 px-4 sm:px-0">
          <div className="relative inline-block">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary/30 via-primary/20 to-primary/30 rounded-lg blur-lg opacity-70"></div>
            <Button asChild size="lg" variant="default" className="px-6 sm:px-8 py-2 sm:py-3 button-modern relative shadow-lg hover:shadow-primary/30 text-sm sm:text-base">
              <Link to="/uslugi">
                {getTranslation("viewFullOffer", language)} <ArrowRight className="ml-1.5 sm:ml-2 h-3 w-3 sm:h-4 sm:w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}